

    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref, Prop, Watch } from "vue-property-decorator";
    import { beforeRouteLeave } from "@/router/router";
    import * as toastr from "toastr";
    import Store from "@/store/store";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import Colours from "@/utilities/Colours";
    import { appEventBus } from "@/utilities/EventBus";
    import FileDownload from "@/utilities/FileDownload";
    import Dirtyness from "@/utilities/Dirtyness";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import AlertDialogue from "@/vue/components/AlertDialogue.vue";
    import WorkPermitPersonPicker from "@/vue/components/WorkPermits/WorkPermitPersonPicker.vue";
    import FileUploadDropZone from "@/vue/components/FileUploadDropZone.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";

    import SouthEasternBeforePart from "@/vue/components/WorkPermits/SouthEastern/BeforePart.vue";
    import SouthEasternPerson from "@/vue/components/WorkPermits/SouthEastern/Person.vue";
    import SouthEasternAfterPart from "@/vue/components/WorkPermits/SouthEastern/AfterPart.vue";
    import SwmScapaBeforePart from "@/vue/components/WorkPermits/SwmScapa/BeforePart.vue";
    import SwmScapaPerson from "@/vue/components/WorkPermits/SwmScapa/Person.vue";
    import SwmScapaAfterPart from "@/vue/components/WorkPermits/SwmScapa/AfterPart.vue";
    import ErrorPart from "@/vue/components/WorkPermits/Error.vue";

    import PrintTextBox from "@/vue/components/PrintForm/PrintTextBox.vue";
    import PrintTextArea from "@/vue/components/PrintForm/PrintTextArea.vue";
    import PrintCheckbox from "@/vue/components/PrintForm/PrintCheckbox.vue";
    import PrintDatePicker from "@/vue/components/PrintForm/PrintDatePicker.vue";
    import PrintDateTimePicker from "@/vue/components/PrintForm/PrintDateTimePicker.vue";
    import PrintSelect from "@/vue/components/PrintForm/PrintSelect.vue";

    import { WorkPermit } from "@/model/workPermits/WorkPermit";
    import { WorkPermitPerson } from "@/model/workPermits/WorkPermitPerson";
    import { ILookupItem } from "@/model/LookupItem";
    import { IWorkPermitLoadResponse } from "@/model/workPermits/WorkPermitLoadResponse";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { IWorkPermitSaveResponse } from "@/model/IWorkPermitSaveResponse";
    import { Visit } from "@/model/Visit";
    import { VisitSearchResult } from "@/model/VisitSearchResult";
    import { Resource } from "@/model/Resource";
    import { WorkPermitFormType, WorkPermitEventType } from "@/model/Enums";
    import { WorkPermitEvent } from "@/model/workPermits/WorkPermitEvent";
    import { Site } from "@/model/Site";
    import { IUser } from "@/model/User";
    import { IVisitSearchResponse } from "@/model/VisitSearchResponse";
    import { VisitSearchParameters } from "@/model/VisitSearchParameters";
import { IFileUploadResponse } from "@/model/IFileUploadResponse";
import { FileSizeOptionStringOrBase } from "filesize";

    enum CloneType {
        None = 0,
        NewNumber = 1,
        SameNumber = 2
    }

    enum ReIssueSaveType {
        None = 0,
        SaveDraft = 1,
        ReIssue = 2
    }

    @Component({
        components: {
            ApiButton,
            PrimaryButton,
            SouthEasternBeforePart,
            SouthEasternPerson,
            SouthEasternAfterPart,
            SwmScapaBeforePart,
            SwmScapaPerson,
            SwmScapaAfterPart,
            ErrorPart,
            AlertDialogue,
            ConfirmDialogue,
            WorkPermitPersonPicker,
            FileUploadDropZone,
            DatePicker,
            PrintDatePicker,
            PrintDateTimePicker,
            PrintTextBox,
            PrintTextArea,
            PrintCheckbox,
            PrintSelect
        },
        beforeRouteLeave
    })
    export default class WorkPermitComponent extends Vue {

        utils = Utils; // access in template

        clientID = this.$store.state.signedInUser?.clientID ?? Utils.emptyGuidValue;

        // This component can be used as a page (in admin section)
        // OR as a component within the signature page.
        // We can tell which depending on whether ID comes from route or from prop...

        isAdminPage = true; // assume this first...

        // used to load data for public form (or else user route parameter)
        @Prop({ default: "" })
        workPermitId!: string;

        async mounted(): Promise<void> {
            // admin or public page?
            this.isAdminPage = !Utils.isEmptyId(this.$route.params.workPermitID ?? Utils.emptyGuidValue);

            try {
                // user route or prop for ID - depending on if is admin or public view
                await this.loadWorkPermit();
            } finally {
                this.isLoading = false; // hide overlay before visits loaded since we only need them for person dialogue
            }
            // visits were taking some time to load - so loading them after the the other bits...
            await this.loadVisits(this.workPermit.siteID);
        }

        // make enums into class properties so that template can see them
        cloneType = CloneType;
        reIssueSaveType = ReIssueSaveType;

        isLoading = true;
        visitorCompanyList: Array<ILookupItem> = [];
        visitList: Array<VisitSearchResult> = [];
        workPermit: WorkPermit = new WorkPermit();

        site = new Site();

        isDirty: boolean = false;
        readonly dirtynessOptions = {
            model: this.workPermit,
            isDeep: true,
            excludeList: [...Dirtyness.defaultAuditFieldList() ]
        };

        get beforePartComponentName(): string {
            if(this.workPermit.formType === WorkPermitFormType.SouthEastern) return "SouthEasternBeforePart";
            if(this.workPermit.formType === WorkPermitFormType.SwmScapa) return "SwmScapaBeforePart";
            return "ErrorPart";
        }

        get personPartComponentName(): string {
            if(this.workPermit.formType === WorkPermitFormType.SouthEastern) return "SouthEasternPerson";
            if(this.workPermit.formType === WorkPermitFormType.SwmScapa) return "SwmScapaPerson";
            return "ErrorPart";
        }

        get afterPartComponentName(): string {
            if(this.workPermit.formType === WorkPermitFormType.SouthEastern) return "SouthEasternAfterPart";
            if(this.workPermit.formType === WorkPermitFormType.SwmScapa) return "SwmScapaAfterPart";
            return "ErrorPart";
        }

        get statusColour(): string {
            const status = this.workPermit.latestStatus;
            if(status === WorkPermitEventType.Draft) return "#CCC";
            // Paul ordered these colours
            if(status === WorkPermitEventType.Issued) return "#1BA4DE";
            if(status === WorkPermitEventType.IssuedExpired) return "#E72275";
            if(status === WorkPermitEventType.Closed) return "#76B94E";
            if(status === WorkPermitEventType.Surrendered) return "#FF9232";
            return "#EEE";
        }

        get statusIcon(): string {
            const status = this.workPermit.latestStatus;
            if(status === WorkPermitEventType.Draft) return "mdi-file-edit-outline";
            if(status === WorkPermitEventType.Issued) return "mdi-check-decagram";
            if(status === WorkPermitEventType.IssuedExpired) return "mdi-check-decagram";
            if(status === WorkPermitEventType.Surrendered) return "mdi-rotate-left";
            if(status === WorkPermitEventType.Closed) return "mdi-progress-close";
            return "mdi-circle";
        }

        get isStatusDark(): boolean {
            return Colours.isDark(this.statusColour);
        }

        get title(): string {
            return Utils.isEmptyId(this.workPermit.id) ? "New Work Permit" : `Edit Work Permit - ${this.workPermit.number}`;
        }

        get logoSource(): string {
            return this.getLogoResource()?.sourceUrl ?? require("@/images/missing.png");
        }

        get isLogo(): boolean {
            return Boolean(this.getLogoResource()?.filename);
        }

        get canDelete(): boolean {
            return !this.workPermit.isNew
                && this.workPermit.latestStatus === WorkPermitEventType.Draft;
        }

        get canIssue(): boolean {
            return !Utils.isEmptyId(this.workPermit.siteID)
                && this.workPermit.latestStatus === WorkPermitEventType.Draft;
                // && !this.hasExpired; - no, show button but prevent issue (show alert)
        }

        get canSign(): boolean {
            return this.workPermit.latestStatus === WorkPermitEventType.Issued
                && !this.workPermit.hasHappened(WorkPermitEventType.Signed);
        }

        get isSigned(): boolean {
            return this.workPermit.hasHappened(WorkPermitEventType.Signed);
        }

        get canClose(): boolean {
            const status = this.workPermit.latestStatus;
            return status === WorkPermitEventType.Issued
                || status === WorkPermitEventType.IssuedExpired
                || status === WorkPermitEventType.Surrendered;
        }

        get isClosed(): boolean {
            return this.workPermit.hasHappened(WorkPermitEventType.Closed);
        }

        get canClone(): boolean {
            const status = this.workPermit.latestStatus;
            return status !== WorkPermitEventType.Draft; //correct logic ???
        }

        // most fields - can't edit even if draft if is re-issue
        get isReadOnly(): boolean {
            return !this.isAdminPage
                || this.workPermit.latestStatus !== WorkPermitEventType.Draft
                || this.workPermit.isReIssue;
        }

        // only some fields can be edited if re-issue
        get isReadOnlyExceptReIssue(): boolean {
            return !this.isAdminPage
                || this.workPermit.latestStatus !== WorkPermitEventType.Draft;
        }

        get pulseRedClass(): any {
            return {
                "pulse-red-longer": this.workPermit.isReIssue && !this.isReadOnlyExceptReIssue
            };
        }

        // can only edit newly added persons since re-issue
        isReadOnlyPerson(person: WorkPermitPerson): boolean {
            return !this.isAdminPage
                || this.workPermit.latestStatus !== WorkPermitEventType.Draft
                || (this.workPermit.isReIssue && !Utils.isEmptyId(person.copiedFromID));
        }

        get visitPersonList(): Array<WorkPermitPerson> {
            const personList = this.visitList.map(v => this.personFromVisit(v.visit));
            return personList;
        }

        getLogoResource(): Resource|null {
            const siteLogo = Store.state.site?.logoResource;
            return siteLogo?.hasFile
                ? siteLogo
                : Store.state.clientSettings?.client?.logoResource;
        }

        getUserFullName(): string {
            const user = Store.state.signedInUser;
            return user == null ? "- - -" : `${user.forename} ${user.surname}`;
        }

        willExpireImmediately(): boolean {
            // if no time, use end of day
            return Utils.hasTime(this.workPermit.validTo)
                ? this.workPermit.validTo < new Date()
                : this.workPermit.validTo < Utils.endOfToday();
        }

        //
        // -- watchers...
        //

        @Watch("$route", { deep: false, immediate: false })
        async onRouteChanged() {
            await this.loadWorkPermit();
        }

        @Watch("workPermitId", { deep: false, immediate: false })
        async onIdChanged() {
            await this.loadWorkPermit();
        }

        @Watch("workPermit", { deep: true, immediate: false })
        onWorkPermitChanged() {
            this.isDirty = Dirtyness.dataChanged(this.dirtynessOptions);
        }

        //
        // --
        //

        async loadWorkPermit(): Promise<void> {
            Utils.resetObject(this.workPermit);
            this.workPermit.formType = WorkPermitFormType.SouthEastern;

            const routeWorkPermitID = this.$route.params.workPermitID ?? Utils.emptyGuidValue;
            this.isAdminPage = !Utils.isEmptyId(routeWorkPermitID);
            if(!this.isAdminPage && Utils.isEmptyOrWhitespace(this.workPermitId)) {
                console.error("workPermitId prop cannot be empty!");
                return;
            }

            // id comes from route or prop - depending on where this is...
            const url = `api/workPermit/Load?id=${this.isAdminPage ? routeWorkPermitID : this.workPermitId}`;
            const response: IWorkPermitLoadResponse = await ApiClient.get(url);
            if(response.workPermit.persons == null) response.workPermit.persons = [];
            this.workPermit.update(response.workPermit);
            this.site.update(response.site);
            this.clientID = this.site.clientID;
            this.visitorCompanyList = response.visitorCompanies;
            //this.workPermitForm?.resetValidation();
            Dirtyness.setClean(this.dirtynessOptions);
        }

        async loadVisits(siteID: string) {
            // don't load visits if public page
            if(!this.isAdminPage) return;

            const searchParameters = new VisitSearchParameters();
            searchParameters.siteID = siteID;
            searchParameters.pageNumber = 0; // get all
            searchParameters.onlyRecentVisits = true;
            const response: IVisitSearchResponse = await ApiClient.post("/Api/Visit/Search", searchParameters);
            this.visitList.length = 0;
            this.visitList.push(...response.list.map(v => new VisitSearchResult(v)));
        }

        goToList(): void {
            this.$router.push({ name: "AdminWorkPermits" });
        }

        print(): void {
            window.print();
        }

        //
        // -- save + delete
        //

        shouldShowIssueSaveDialogue: boolean = false;
        reIssueSaveOption: ReIssueSaveType = ReIssueSaveType.None;

        @Watch("reIssueSaveOption", { deep: false, immediate: false })
        onReIssueSaveOptionChanged() {
            if(this.reIssueSaveOption === ReIssueSaveType.ReIssue) {
                // this adds a history item...
                this.workPermit.issuedWhen = new Date();
                this.workPermit.issuedByWho = this.getUserFullName();
            }
            else {
                this.workPermit.removeHistory(WorkPermitEventType.Issued);
            }
        }

        async save(): Promise<void> {
            // if (!this.workPermitForm.validate()) {
            //     toastr.warning("Please fix highlighted issues", "Cannot Save");
            //     return;
            // }

            // could maybe incorporate into form validation... (meh)
            if(Utils.hasDateValue(this.workPermit.validFrom)
                && Utils.hasDateValue(this.workPermit.validTo)
                && this.workPermit.validFrom > this.workPermit.validTo){
                toastr.warning("Valid-To cannot be before Valid-From", "Cannot Save");
                return;
            }

            if (this.workPermit.isReIssue && this.checkCanIssue(false)) {
                this.reIssueSaveOption = ReIssueSaveType.None;
                this.shouldShowIssueSaveDialogue = true;
            }
            else {
                await this.doSave();
            }
        }

        cancelReIssueSave() {
            this.workPermit.removeHistory(WorkPermitEventType.Issued);
            this.shouldShowIssueSaveDialogue = false;
            this.reIssueSaveOption = ReIssueSaveType.None;
        }

        async doSave(savedMessage: string = "Saved"): Promise<void> {
            const response: IWorkPermitSaveResponse = await ApiClient.post("/api/workPermit/save", this.workPermit, "work-permit-save");
            if(this.workPermit.isNew) this.workPermit.id = response.newID; // this is redundant since created new via dialogue on list page
            this.workPermit.history = response.history.map(e => new WorkPermitEvent(e));
            this.workPermit.resources = response.resources.map(r => new Resource(r)); // any new resources might need IDs...
            this.workPermit.attachmentResourceIDs = response.resources.map(r => r.id);
            this.isDirty = Dirtyness.setClean(this.dirtynessOptions);
            this.shouldShowIssueDialogue = false;
            this.shouldShowIssueSaveDialogue = false;
            toastr.success(savedMessage);
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/workPermit/delete", this.workPermit, "work-permit-delete");
            // hmmm... (just to trigger toast on workPermit list)
            setTimeout(() => {
                appEventBus.emit("deleted-work-permit", this.workPermit.number);
            }, 500);
            this.goToList();
        }

        //
        // -- personnel
        //

        // updates visitPersonList (computed + bound in person picker)
        onTogglePerson(toggledPerson: WorkPermitPerson): void {
            const formPersonIndex = this.workPermit.persons.findIndex(p => p.visitID === toggledPerson.visitID);
            if(toggledPerson.isSelected && formPersonIndex === -1) {
                this.workPermit.persons.push(toggledPerson);
            }
            if(!toggledPerson.isSelected && formPersonIndex !== -1) {
                this.workPermit.persons.splice(formPersonIndex, 1);
            }
            this.selectDefaultMainPerson();
        }

        onAddPerson(name: string, email: string): void {
            const person = new WorkPermitPerson();
            person.name = name;
            person.email = email;
            this.workPermit.persons.push(person);
            this.selectDefaultMainPerson();
        }

        // event from person component
        onSelectMainPerson(person: WorkPermitPerson): void {
            if(this.isReadOnly) return;
            this.workPermit.persons.forEach(p => p.isMain = false);
            person.isMain = true;

            const mainIndex = this.workPermit.persons.indexOf(person);
            console.log("### main index = ", mainIndex);

            if(mainIndex > 0) {
                this.workPermit.persons.unshift(this.workPermit.persons.splice(mainIndex, 1)[0]);
            }
        }

        selectDefaultMainPerson() {
            if (this.workPermit.persons.length === 0) return;
            if (this.workPermit.persons.find(p => p.isMain) != null) return;
            const person = this.workPermit.persons[0];
            this.onSelectMainPerson(person);
            toastr.info("has been set as the main contact.", person.name);
        }

        updatePersons(personList: Array<WorkPermitPerson>): void {
            const personsSelected = personList.filter(p => p.isSelected);
            this.workPermit.persons.splice(0);
            this.workPermit.persons.push(...personsSelected);
        }

        choosePersons(): void {
            const personPicker: WorkPermitPersonPicker = this.$refs.personPicker as WorkPermitPersonPicker;
            personPicker.show();
        }

        removePerson(person: WorkPermitPerson): void {
            if(!person) return;
            const i = this.workPermit.persons.indexOf(person);
            if(i > -1) this.workPermit.persons.splice(i, 1);
            this.selectDefaultMainPerson();
        }

        personFromVisit(visit: Visit): WorkPermitPerson {
            const person = new WorkPermitPerson();
            person.visitID = visit.id;
            person.name = `${visit.forename} ${visit.surname}`;
            person.email = visit.email;
            person.phone = "";
            person.visitCode = visit.code;
            person.visitIsCompleteAndPassed = Utils.hasDateValue(visit.completed) && visit.answerCount > 0 && !visit.hasFailed;
            person.plannedArrival = visit.plannedArrival;
            // finding answer by label - sketchy...
            person.southEasternFieldSet.ptsAtocNumber = visit.extraRegistrationAnswers.find(a => a.label === "PTS Number")?.value ?? "";
            person.isSelected = (this.workPermit.persons?.findIndex(p => p.visitID === person.visitID) ?? -1) > -1;
            return person;
        }

        //
        // -- issue
        //

        shouldShowIssueDialogue: boolean = false;

        checkCanIssue(shouldShowAlert: boolean): boolean {
            const problems: Array<string> = [];
            if (!this.canIssue) problems.push("Can only issue if draft!"); // not expecting this to happen
            if (this.willExpireImmediately()) problems.push("Please check the 'Valid Until' date.  It needs to be in the future.");
            if (this.workPermit.persons.length === 0) problems.push("You have not added any personnel.  There needs to be a main contact.");
            if(problems.length > 0 && shouldShowAlert) {
                const html = `<ul><li>${problems.join("</li><li>")}</li></ul>`;
                console.log("html = ", html);
                this.showAlertDialogue("Cannot Issue", html);
            }
            return problems.length === 0;
        }

        showIssueDialogue(): void {
            if(!this.checkCanIssue(true)) return;
            this.workPermit.issuedWhen = new Date();
            this.workPermit.issuedByWho = this.getUserFullName();
            this.shouldShowIssueDialogue = true;
        }

        cancelIssue(): void {
            this.workPermit.removeHistory(WorkPermitEventType.Issued);
            this.shouldShowIssueDialogue = false;
        }

        async saveIssued(): Promise<void> {
            if(!this.workPermit.hasHappened(WorkPermitEventType.Issued)) {
                // this shouldn't happen!
                toastr.error("Fields aren't set!")
                return;
            }
            const doSaveIssued = async() => {
                await this.doSave("Issued Permit");
                this.showSignatureDialogue();
            }
            if(this.willExpireImmediately()) {
                const saveConfirmDialogue = this.$refs?.saveConfirmDialogue as ConfirmDialogue;
                saveConfirmDialogue.text = "The <b>Valid Until</b> field is in the past.<br>If you issue this now, it will be expired and you will not be able to edit it!";
                saveConfirmDialogue.showWithConfirmCallback(doSaveIssued);
            }
            else{
                doSaveIssued();
            }
        }

        //
        // -- signature
        //

        shouldShowSignatureDialogue: boolean = false;

        getMainContact = () => this.workPermit?.persons?.find(p => p.isMain && Utils.isValidEmail(p.email));

        get mainContactName(): string {
            return this.getMainContact()?.name ?? "- - -";
        }

        get mainContactEmail(): string {
            return this.getMainContact()?.email ?? "- - -";
        }

        showSignatureDialogue(): void {
            const mainContact = this.getMainContact();
            if(mainContact == null) {
                this.showAlertDialogue("Cannot Sign", "There needs to be a <b>main contact</b> with a valid email address.");
            }
            else {
                this.shouldShowSignatureDialogue = true;
            }
        }

        cancelSignature(): void {
            this.shouldShowSignatureDialogue = false;
        }

        async sendSignatureRequest(): Promise<void> {
            const mainContact = this.getMainContact();
            if(mainContact == null) return;

            //this.workPermit.status = WorkPermitEventType.Signatured;
            const response: IWorkPermitSaveResponse = await ApiClient.post("/api/workPermit/sendSignatureRequest", this.workPermit, "send-signature-request");
            if(this.workPermit.isNew) this.workPermit.id = response.newID;
            this.workPermit.history = response.history.map(e => new WorkPermitEvent(e));
            this.isDirty = Dirtyness.setClean(this.dirtynessOptions);
            this.shouldShowSignatureDialogue = false;
            if (response.isSuccess) toastr.success(mainContact.email ,"Sent Request");
            else toastr.warning(mainContact.email ,"Sending Failed");
        }

        //
        // -- close
        //

        shouldShowCloseDialogue: boolean = false;

        showCloseDialogue(): void {
            this.workPermit.closedWhen = new Date();
            this.workPermit.closedByWho = this.getUserFullName();
            this.shouldShowCloseDialogue = true;
        }

        cancelClose(): void {
            this.workPermit.removeHistory(WorkPermitEventType.Closed);
            this.shouldShowCloseDialogue = false;
        }

        async saveClosed(): Promise<void> {
            const user: IUser = this.$store.state.signedInUser;

            if(!this.workPermit.hasHappened(WorkPermitEventType.Closed)) {
                // this shouldn't happen!
                toastr.error("Fields aren't set!")
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/workPermit/save", this.workPermit, "work-permit-save");
            if(this.workPermit.isNew) this.workPermit.id = response.newID;
            this.isDirty = Dirtyness.setClean(this.dirtynessOptions);
            this.shouldShowCloseDialogue = false;
            toastr.success("Closed Permit");
        }

        //
        // -- clone
        //

        shouldShowCloneDialogue: boolean = false;

        typeOfClone: CloneType = CloneType.None;

        showCloneDialogue(): void {
            this.typeOfClone = CloneType.None;
            this.isDirty = Dirtyness.dataChanged(this.dirtynessOptions); // belt and braces - make sure flag is up to date
            if(this.isDirty) {
                this.showAlertDialogue("Cannot Copy", "Please save any changes first.");
            }
            else {
                this.shouldShowCloneDialogue = true;
            }
        }

        cancelClone(): void {
            this.shouldShowCloneDialogue = false;
        }

        async sendCloneRequest(): Promise<void> {
            if (this.typeOfClone === CloneType.None) return;
            const response: IWorkPermitSaveResponse = await ApiClient.post(`/api/workPermit/clone?isReIssue=${(this.typeOfClone === CloneType.SameNumber ? '1' : '0')}`, this.workPermit, "send-clone-request");
            this.$router.push({ name: "AdminWorkPermit", params: { workPermitID: response.newID } });
        }

        //
        // -- attachments
        //

        // addAttachment() {
        //     const dialogue: WorkPermitAttachmentDialogue = this.$refs.attachmentDialogue as WorkPermitAttachmentDialogue;
        //     dialogue.show();
        // }

        onFilesQueued(fileNames: Array<string>) {
            console.log("⚡ newly queued: ", fileNames);

            //if we re-uploaded any files, we should REPLACE (or update) them in our queue...
            for(let i = 0; i < fileNames.length; i++) {
                const fileName = fileNames[i];
                const existing = this.workPermit.resources.find(r => r.originalFilename === fileName);
console.log(` > ${fileNames[i]} : `, existing);

                if(existing) {
                    existing.update(new Resource(fileNames[i]));
                }
                else {
                    this.workPermit.resources.push(new Resource(fileNames[i]));
                }
            }
            console.log("importFiles: ", this.workPermit.resources);
        }

        onFileUploaded(uploadResponse: IFileUploadResponse) {
            const resource = this.workPermit.resources.find(f => f.originalFilename === uploadResponse.originalFilename);
            if(!resource) {
                // or we could just add it?
                toastr.error(uploadResponse.originalFilename, "Could Not Find");
                return;
            }
            resource.httpContentType = uploadResponse.httpContentType;
            resource.originalFilename = uploadResponse.originalFilename;
            resource.filename = uploadResponse.filename;
            resource.size = uploadResponse.size;
        }

        removeAttachment(attachment: Resource) {
            const index = this.workPermit.resources.indexOf(attachment);
            if (index > -1) this.workPermit.resources.splice(index, 1); // server-side logic needs to delete...
            toastr.info(`${attachment.originalFilename} removed`);
        }

        iconUrl(resource: Resource): string {
            if(!resource.hasFile) return ApiClient.resolveUrl("api/file/icon?extension=nul");
            const filename = resource.filename;
            const dotAt = filename.lastIndexOf(".");
            const extension = dotAt === -1 || dotAt >= filename.length - 1 ? "" : filename.substr(dotAt + 1).toLowerCase();
            return ApiClient.resolveUrl(`api/file/icon?extension=${extension}`);
        }

        async download(resource: Resource) {
            if(!resource.hasFile) return;
            FileDownload.download(resource);
        }

        //
        // -- stuff
        //

        showAlertDialogue(title: string, text: string): void {
            const dialogue = this.$refs.alertDialogue as AlertDialogue;
            dialogue.title = title;
            dialogue.text = text;
            dialogue.show();
        }
    }
